import { useRef } from 'react'
import RiggingDrawCanvas from './rigging-draw-canvas'
import type { DiagramFile } from './persistence'

export type { DiagramFile } from './persistence'
export type { DrawingElement, Calibration } from './types'
export { toBackendSchema, fromBackendSchema } from './schema-adapter'
export type { BackendCanvasData, BackendElement } from './schema-adapter'

export type RiggingCanvasProps = {
  /** Seed diagram loaded on mount.  Pass `null` for a blank canvas. */
  value: DiagramFile | null
  /** Called (debounced) whenever the diagram changes. */
  onChange: (next: DiagramFile) => void
  /** Ref populated with a function that returns a PNG data-URL of the stage. */
  exportPngRef?: React.RefObject<(() => string | null) | null>
  /** Stable document-scoped key for local draft recovery. */
  draftKey?: string
  /** Signature of the latest diagram state confirmed by the backend. */
  persistedSignature?: string | null
  /** Immutable latest revision number confirmed by the backend. */
  persistedRevision?: number | null
  /** True while an existing diagram's backend value is still loading. */
  loading?: boolean
}

/**
 * Reusable rigging-draw editor wired to a controlled DiagramFile value.
 *
 * No API wiring — this is a pure client-side component.  The parent owns
 * persistence and can call `exportPngRef.current()` to grab a PNG snapshot.
 */
export function RiggingCanvas({
  value,
  onChange,
  exportPngRef,
  draftKey,
  persistedSignature,
  persistedRevision,
  loading = false,
}: RiggingCanvasProps) {
  const onChangeRef = useRef<((next: DiagramFile) => void) | null>(onChange)
  onChangeRef.current = onChange

  return (
    <RiggingDrawCanvas
      initialValue={value}
      onChangeRef={onChangeRef}
      exportPngRef={exportPngRef}
      draftKey={draftKey}
      persistedSignature={persistedSignature}
      persistedRevision={persistedRevision}
      loading={loading}
    />
  )
}
